import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { linkageService } from '../services/linkageService';

interface DynamicLinkageCodeProps {
  userId?: string;
  onCodeGenerated?: (code: string) => void;
}

const DynamicLinkageCode: React.FC<DynamicLinkageCodeProps> = ({
  userId,
  onCodeGenerated
}) => {
  const { t } = useTranslation();
  const [currentCode, setCurrentCode] = useState<string>('');
  const [isScanning] = useState<boolean>(false);

  // Colores para los segmentos (contraste alto para escaneo)
  const segmentColors = [
    '#000000', // Negro
    '#FFFFFF', // Blanco
    '#FF0000', // Rojo
    '#00FF00', // Verde
    '#0000FF', // Azul
    '#FFFF00', // Amarillo
    '#FF00FF', // Magenta
    '#00FFFF'  // Cyan
  ];

  // Generar y actualizar código usando el servicio
  useEffect(() => {
    const generateAndUpdateCode = async () => {
      try {
        if (!userId) return;
        
        const linkageCode = await linkageService.generateEmployeeCode(userId);
        setCurrentCode(linkageCode.code);
        onCodeGenerated?.(linkageCode.code);
      } catch (error) {
        console.error('Error generating linkage code:', error);
      }
    };

    generateAndUpdateCode(); // Código inicial
    const interval = setInterval(generateAndUpdateCode, 30000);

    return () => clearInterval(interval);
  }, [userId, onCodeGenerated]);



  return (
    <div className="flex flex-col items-center justify-center p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl">
      {/* Código Visual Dinámico Hexagonal */}
      <div className="relative w-80 h-80 mb-6">
        {/* Contenedor hexagonal principal */}
        <div className="w-full h-full relative">
          {/* Hexágono exterior */}
          <div className="absolute inset-0">
            <svg viewBox="0 0 320 320" className="w-full h-full">
              <defs>
                <linearGradient id="hexGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style={{stopColor: '#3B82F6', stopOpacity: 0.1}} />
                  <stop offset="50%" style={{stopColor: '#8B5CF6', stopOpacity: 0.1}} />
                  <stop offset="100%" style={{stopColor: '#EC4899', stopOpacity: 0.1}} />
                </linearGradient>
              </defs>
              <polygon 
                points="160,20 280,80 280,200 160,260 40,200 40,80" 
                fill="url(#hexGradient)"
                stroke="#000000"
                strokeWidth="3"
                className="animate-pulse"
              />
            </svg>
          </div>
          
          {/* Patrón hexagonal dinámico */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="grid grid-cols-7 gap-1 transform rotate-12">
              {Array.from({ length: 49 }).map((_, index) => {
                const row = Math.floor(index / 7);
                const col = index % 7;
                const charCode = currentCode.charCodeAt(index % currentCode.length) || 65;
                const shouldShow = (charCode + row + col) % 2 === 0;
                const colorIndex = (charCode + row + col) % segmentColors.length;
                const delay = index * 0.05;
                
                return (
                  <div
                    key={index}
                    className={`w-8 h-8 transition-all duration-500 transform hover:scale-110 ${
                      shouldShow ? 'opacity-100' : 'opacity-30'
                    }`}
                    style={{
                      backgroundColor: shouldShow ? segmentColors[colorIndex] : '#f0f0f0',
                      animation: shouldShow 
                        ? `hexPulse 2s infinite ${delay}s, hexRotate 4s ease-in-out infinite ${delay}s` 
                        : 'none',
                      clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)'
                    }}
                  />
                );
              })}
            </div>
          </div>
          
          {/* Marcadores hexagonales de esquina */}
          <div className="absolute top-4 left-4">
            <div 
              className="w-6 h-6 bg-white border-2 border-black animate-bounce"
              style={{ 
                clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                animationDelay: '0s',
                animationDuration: '2s'
              }}
            />
          </div>
          <div className="absolute top-4 right-4">
            <div 
              className="w-6 h-6 bg-white border-2 border-black animate-bounce"
              style={{ 
                clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                animationDelay: '0.5s',
                animationDuration: '2s'
              }}
            />
          </div>
          <div className="absolute bottom-4 left-4">
            <div 
              className="w-6 h-6 bg-white border-2 border-black animate-bounce"
              style={{ 
                clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                animationDelay: '1s',
                animationDuration: '2s'
              }}
            />
          </div>
          
          {/* Código central hexagonal */}
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <div 
              className="bg-white px-4 py-2 rounded-lg border-2 border-black shadow-lg animate-pulse transform -rotate-12"
              style={{ animationDuration: '3s' }}
            >
              <div className="text-lg font-mono font-bold text-black tracking-wider">
                {currentCode}
              </div>
            </div>
          </div>
          
          {/* Partículas hexagonales flotantes */}
          <div className="absolute inset-0 pointer-events-none">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="absolute w-3 h-3 bg-yellow-400 animate-ping"
                style={{
                  left: `${15 + (i * 10)}%`,
                  top: `${20 + (i * 8)}%`,
                  animationDelay: `${i * 0.3}s`,
                  animationDuration: '2s',
                  clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)'
                }}
              />
            ))}
          </div>
        </div>
        
        {/* Indicador de escaneo hexagonal */}
        <div className="absolute -top-2 -right-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-bold animate-pulse">
          SCAN
        </div>
        
        {/* Efecto de escaneo hexagonal */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="w-full h-1 bg-gradient-to-r from-transparent via-green-400 to-transparent animate-scan" />
        </div>
      </div>

      {/* Código alfanumérico */}
      <div className="text-center mb-4">
        <div className="text-2xl font-mono font-bold text-gray-800 tracking-wider mb-2">
          {currentCode}
        </div>
        <div className="text-sm text-gray-600">
          {t('dynamicLinkageCode.refreshesEvery30s', 'Refreshes every 30 seconds')}
        </div>
      </div>



      {/* Estado de escaneo */}
      {isScanning && (
        <div className="flex items-center gap-2 text-blue-600 animate-pulse">
          <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" />
          <span className="text-sm font-medium">
            {t('dynamicLinkageCode.scanning', 'Scanning for connection...')}
          </span>
        </div>
      )}

      {/* Instrucciones */}
      <div className="text-center text-sm text-gray-600 mt-4 max-w-xs">
        <p>
          {t('dynamicLinkageCode.instructions', 'This dynamic code is scannable by any barcode scanner app. Show it to your company administrator.')}
        </p>
      </div>
    </div>
  );
};

export default DynamicLinkageCode; 